// File-level imports and component definition (AvailableClipsTable)
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useQuery } from '@tanstack/react-query';
import AleaTable from '../../components/AleaTable/AleaTable';
import { ITableColumn } from '../../interfaces';
import { Box, Text, Flex, Button, Grid, GridItem, Image } from '@chakra-ui/react';
import AleaLabel from '../../components/AleaLabel/AleaLabel';
import { IClip } from '../../interfaces/Clip/IClip.ts';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../constants/UserRoles.ts';
import AleaChevronRightIcon from '../../icons/AleaChevronRightIcon';
import AleaChevronDownIcon from '../../icons/AleaChevronDownIcon';
import AleaDockingStationIcon from '../../icons/AleaDockingStationIcon';
import AleaClipOpenIcon from '../../icons/AleaClipOpenIcon';
import GatewayService from '../../services/GatewayService';
import ClipService from '../../services/ClipService';
import { IGateway } from '../../interfaces/Gateway/IGateway';
import checkIcon from '/assets/images/icons/check.svg';
import { useToast } from '@chakra-ui/react';
import AleaProgressModal from '../../components/AleaProgressModal/AleaProgressModal';
import AleaDropdownAlpha from '../../components/AleaDropdownAlpha/AleaDropdownAlpha';
import AleaConfirmModal from '../../components/AleaModal/AleaConfirmModal';

interface ExtendedClip extends IClip {
  expandIcon?: string;
  [key: string]: unknown;
}

interface AvailableClipsTableProps {
  clips: IClip[];
  total: number;
  isLoading?: boolean;
  onSort?: (field: string, order: 'asc' | 'desc') => void;
  orderBy?: string;
  sortOrder?: 'asc' | 'desc';
  onRefresh?: () => Promise<void>;
  onProgressModalChange?: (isOpen: boolean) => void;
  onStatsRefresh?: () => Promise<void>;
}

export type AvailableClipsTableHandles = {
  hasPendingChanges: () => boolean;
  savePendingChanges: () => Promise<void>;
  discardPendingChanges: () => void;
  resetExpandedRow: () => void;
};

const AvailableClipsTable = forwardRef<AvailableClipsTableHandles, AvailableClipsTableProps>(({
  clips,
  total,
  isLoading = false,
  onSort,
  orderBy,
  sortOrder,
  onRefresh,
  onProgressModalChange,
  onStatsRefresh,
}, ref) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [selectedGateway, setSelectedGateway] = useState<Record<string, string>>({});
  const [initialSelectedGateway, setInitialSelectedGateway] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [dockEditMode, setDockEditMode] = useState<Record<string, boolean>>({});
  const [pendingClipUnlinks, setPendingClipUnlinks] = useState<Record<string, boolean>>({});
  const [initialDockEditMode, setInitialDockEditMode] = useState<Record<string, boolean>>({});
  
  // Add modal state for unsaved changes warning
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [pendingRowAction, setPendingRowAction] = useState<{
    targetRowId: string;
    action: 'close' | 'switch';
  } | null>(null);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    hasPendingChanges: () => {
      return Object.keys(selectedGateway).length > 0 ||
             Object.keys(pendingClipUnlinks).length > 0 ||
             Object.keys(dockEditMode).length > 0;
    },
    savePendingChanges: async () => {
      if (expandedRowId) {
        await handleSubmit(expandedRowId);
      }
    },
    discardPendingChanges: () => {
      if (expandedRowId) {
        handleCancelChanges(expandedRowId);
      }
    },
    resetExpandedRow: () => {
      setExpandedRowId(null);
    }
  }));

  const gatewaysQuery = useQuery({
    queryKey: ['availableClipsTableGateways', user?.carehome?.id] as const,
    enabled: !!user?.carehome?.id,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnReconnect: false,
    queryFn: () =>
      GatewayService.getGateways({
        carehome_id: user!.carehome!.id,
      }),
    select: (response) => response.items,
  });

  const gateways: IGateway[] = gatewaysQuery.data ?? [];

  // Notify parent about progress modal state changes
  useEffect(() => {
    if (onProgressModalChange) {
      onProgressModalChange(isProgressModalOpen);
    }
  }, [isProgressModalOpen, onProgressModalChange]);



  // Helper function to check if there are any pending changes
  const hasPendingChanges = (clipId: string) => {
    // Only check for changes specific to this clip
    return !!(
      pendingClipUnlinks[clipId] ||
      (selectedGateway[clipId] !== initialSelectedGateway[clipId]) ||
      (dockEditMode[clipId] !== initialDockEditMode[clipId])
    );
  }

  const handleRowClick = (item: ExtendedClip) => {
    // Check if current expanded row has pending changes
    if (expandedRowId && hasPendingChanges(expandedRowId)) {
      // User is trying to close or switch - show confirmation modal
      if (expandedRowId === item.id) {
        // Trying to close the current row
        setPendingRowAction({ targetRowId: item.id, action: 'close' });
      } else {
        // Trying to switch to a different row
        setPendingRowAction({ targetRowId: item.id, action: 'switch' });
      }
      setIsUnsavedChangesModalOpen(true);
      return;
    }
    
    // No pending changes, proceed normally
    setExpandedRowId(expandedRowId === item.id ? null : item.id);
    // Reset selections when opening a new row
    if (expandedRowId !== item.id) {
      // If the clip has a gateway, auto-select its serial
      if (item.gateway?.id) {
        const gw = gateways.find(g => g.id === item.gateway?.id);
        if (gw) {
          setSelectedGateway(prev => ({ ...prev, [item.id]: gw.serial }));
          setInitialSelectedGateway(prev => ({ ...prev, [item.id]: gw.serial }));
        } else {
          setSelectedGateway(prev => ({ ...prev, [item.id]: '' }));
          setInitialSelectedGateway(prev => ({ ...prev, [item.id]: '' }));
        }
        // If a dock is assigned, disable the dropdown initially
        setDockEditMode(prev => ({ ...prev, [item.id]: false }));
        setInitialDockEditMode(prev => ({ ...prev, [item.id]: false }));
      } else {
        setSelectedGateway(prev => ({ ...prev, [item.id]: '' }));
        setInitialSelectedGateway(prev => ({ ...prev, [item.id]: '' }));
        // If no dock, dropdown active
        setDockEditMode(prev => ({ ...prev, [item.id]: true }));
        setInitialDockEditMode(prev => ({ ...prev, [item.id]: true }));
      }
      // Clear the deactivation flag when the row opens
      setPendingClipUnlinks(prev => ({ ...prev, [item.id]: false }));
    }
  };

  const handleSubmit = async (clipId: string) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setProgressMessage(t('savingChanges'));
    setIsProgressModalOpen(true);

    try {
      if (pendingClipUnlinks[clipId]) {
        // Unlink flow
        await ClipService.deAssignClipFromGateway(clipId);

        toast({
          title: t('success'),
          description: 'Clip unlinked successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Assignment flow
        if (!selectedGateway[clipId]) {
          return;
        }
        const gateway = gateways.find(g => g.serial === selectedGateway[clipId]);
        if (!gateway) {
          return;
        }

        await ClipService.assignClipToGateway(clipId, gateway.id);

        toast({
          title: t('success'),
          description: t('clip_assigned_successfully'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      // Reset selections and close the row
      setSelectedGateway(prev => {
        const newState = { ...prev };
        delete newState[clipId];
        return newState;
      });
      setInitialSelectedGateway(prev => {
        const newState = { ...prev };
        delete newState[clipId];
        return newState;
      });
      setExpandedRowId(null);
      setPendingClipUnlinks(prev => ({ ...prev, [clipId]: false }));
      setDockEditMode(prev => ({ ...prev, [clipId]: false }));
      setInitialDockEditMode(prev => {
        const newState = { ...prev };
        delete newState[clipId];
        return newState;
      });

      // Refresh the list
      if (onRefresh) {
        await onRefresh();
      }
      // Refresh care home stats after successful submit
      if (onStatsRefresh) {
        await onStatsRefresh();
      }
    } catch (error) {
      console.error('Failed to save clip assignment/unlink:', error);
      toast({
        title: t('error'),
        description: pendingClipUnlinks[clipId]
          ? 'Failed to unlink clip'
          : t('failed_to_assign_clip'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      setIsProgressModalOpen(false);
    }
  };

  const handleCancelChanges = (clipId: string) => {
    // Show progress modal for cancel operation
    setProgressMessage(t('changesAreBeingDiscarded'));
    setIsProgressModalOpen(true);

    // Close the expanded row immediately
    setExpandedRowId(null);

    setTimeout(() => {
      // Clear all pending changes for this clip
      setSelectedGateway(prev => {
        const newState = { ...prev };
        delete newState[clipId];
        return newState;
      });
      setInitialSelectedGateway(prev => {
        const newState = { ...prev };
        delete newState[clipId];
        return newState;
      });
      setPendingClipUnlinks(prev => {
        const newState = { ...prev };
        delete newState[clipId];
        return newState;
      });
      setDockEditMode(prev => {
        const newState = { ...prev };
        delete newState[clipId];
        return newState;
      });
      setInitialDockEditMode(prev => {
        const newState = { ...prev };
        delete newState[clipId];
        return newState;
      });

      // Hide progress modal
      setIsProgressModalOpen(false);
      setProgressMessage('');
    }, 2000); // 2 seconds for better UX perception
  };

  // Handle modal actions for unsaved changes
  const handleSaveAndProceed = async () => {
    setIsUnsavedChangesModalOpen(false);
    setProgressMessage(t('savingChanges'));
    setIsProgressModalOpen(true);
    
    try {
      if (expandedRowId) {
        await handleSubmit(expandedRowId);
      }
      
      // Handle row actions after save
      if (pendingRowAction?.action === 'close') {
        setExpandedRowId(null);
      } else if (pendingRowAction?.action === 'switch') {
        setExpandedRowId(pendingRowAction.targetRowId);
      }
    } finally {
      // Clean up pending action
      setPendingRowAction(null);
    }
  };

  const handleDiscardAndProceed = () => {
    setIsUnsavedChangesModalOpen(false);
    setProgressMessage(t('changesAreBeingDiscarded'));
    setIsProgressModalOpen(true);
    
    setTimeout(() => {
      if (expandedRowId) {
        handleCancelChanges(expandedRowId);
      }
      
      // Proceed with pending action
      if (pendingRowAction) {
        if (pendingRowAction.action === 'close') {
          setExpandedRowId(null);
        } else if (pendingRowAction.action === 'switch') {
          setExpandedRowId(pendingRowAction.targetRowId);
        }
      }
      
      setIsProgressModalOpen(false);
      setPendingRowAction(null);
    }, 1000); // 1000ms for better UX perception
  };

  // Just close modal without any action
  const handleCloseModal = () => {
    setIsUnsavedChangesModalOpen(false);
  };

  const columns: ITableColumn<ExtendedClip>[] = [
    ...(user?.role !== UserRole.ORGANISATION_MANAGER
      ? [
          {
            key: 'expandIcon',
            label: '',
            sortable: false,
            width: '50px',
            render: (data: ExtendedClip) => (
              <Box
                cursor="pointer"
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                {expandedRowId === data.id ? (
                  <AleaChevronDownIcon />
                ) : (
                  <AleaChevronRightIcon />
                )}
              </Box>
            ),
          },
        ]
      : []),
    {
      key: 'name',
      label: t('residents'),
      sortable: true,
      width: user?.role === UserRole.ORGANISATION_MANAGER ? '25%' : '30%',
      render: (data: ExtendedClip) => (
        <Text>
          {data.gateway?.resident
            ? `${data.gateway.resident.first_name} ${data.gateway.resident.last_name}`
            : '-'}
        </Text>
      ),
      onSort: onSort ? () => onSort('resident__name', sortOrder === 'asc' ? 'desc' : 'asc') : undefined,
      sortOrder: orderBy === 'resident__name' ? sortOrder : undefined,
    },
    {
      key: 'is_active',
      label: t('room'),
      sortable: false,
      width: user?.role === UserRole.ORGANISATION_MANAGER ? '20%' : '25%',
      render: (data: ExtendedClip) => (
        <Text>{data.gateway?.resident?.room || '-'}</Text>
      ),
    },
    {
      key: 'serial',
      label: t('dockingStation'),
      sortable: true,
      width: user?.role === UserRole.ORGANISATION_MANAGER ? '20%' : '25%',
      render: (data: ExtendedClip) => (
        <Text>{data.gateway?.serial || '-'}</Text>
      ),
      onSort: onSort ? () => onSort('serial', sortOrder === 'asc' ? 'desc' : 'asc') : undefined,
      sortOrder: orderBy === 'serial' ? sortOrder : undefined,
    },
    {
      key: 'clip_serial',
      label: t('clips'),
      sortable: false,
      width: user?.role === UserRole.ORGANISATION_MANAGER ? '25%' : '30%',
      render: (data: ExtendedClip) => (
        <Flex gap="8px" flexWrap="wrap" minWidth={0} width="100%">
          <AleaLabel isTruncated maxW="100%" overflow="hidden" whiteSpace="nowrap">
            {data.serial || '-'}
          </AleaLabel>
        </Flex>
      ),
    },
  ];

  function renderExpandedRow(item: ExtendedClip) {
  
    const gatewayOptions = gateways.map((gateway) => {
      const r = gateway.resident;
      const residentLabel = r
        ? `${r.last_name}, ${r.first_name} (${t('general.zi')} ${r.room})`
        : '-';
      const clipsCount = gateway.clips?.length ?? 0;
  
      return {
        value: gateway.serial,
        label: `${residentLabel} - ${gateway.serial}`,
        keywords: `${r?.first_name ?? ''} ${r?.last_name ?? ''} ${r?.room ?? ''} ${gateway.serial ?? ''}`,
        content: (
          <Flex justifyContent="space-between" alignItems="center" width="100%" minWidth={0}>
            <Box width="260px" minWidth="260px" maxWidth="260px" pr="12px">
              <Text isTruncated lineHeight="1.3" title={residentLabel}>
                {residentLabel}
              </Text>
            </Box>
  
            <Flex alignItems="center" gap="8px" justifyContent="flex-start" minWidth={0} width="100%">
              <Flex w="200px" minWidth={0}>
                <Flex
                border="1px solid #DCDCDC"
                borderRadius="999px"
                px="8px"
                py="4px"
                bg="white"
                w="100%"
              >
                <Text fontSize="15px" isTruncated maxW="100%">
                  {gateway.serial}
                </Text>
              </Flex>
              </Flex>
              <Box flex="1" minWidth={0}>
                <Text fontSize="15px" isTruncated>
                  + {clipsCount} {t('clips')}
                </Text>
              </Box>
            </Flex>
          </Flex>
        ),
      };
    });
  
    return (
      <Box p={4}>
        <Box borderRadius="12px" py={6} px={24} fontSize="18px">
          <Grid templateColumns="minmax(0, 1fr) minmax(0, 1fr)" gap={6}>
            {/* Dock */}
            <GridItem>
              <Box width="100%">
                <Flex direction="column" alignItems="flex-start">
                  <Box mb="15px">
                    <AleaDockingStationIcon size="75" />
                  </Box>
                </Flex>
                <Flex direction="column" gap={2} width="100%">
                  <Box width="100%">
                    <AleaDropdownAlpha
                      label={t('dockingStation')}
                      value={selectedGateway[item.id] || ''}
                      options={gatewayOptions}
                      onChange={(value) => {
                        setSelectedGateway(prev => ({ ...prev, [item.id]: value }));
                      }}
                      placeholder={t('selectGateway')}
                      searchPlaceholder={'Name, Zimmer, ...'}
                      isDisabled={!!item.gateway?.id && !dockEditMode[item.id]}
                      menuPosition="fixed"
                      menuWidth={640}
                    />
                  </Box>
                  {!!item.gateway?.id && !dockEditMode[item.id] && (
                    <Text
                      color="#EF5C36"
                      fontSize="15px"
                      fontWeight={300}
                      cursor="pointer"
                      textDecoration="underline"
                      textAlign="left"
                      onClick={() => {
                        // Deactivate: enable dropdown + clear selection -> placeholder visible
                        setDockEditMode(prev => ({ ...prev, [item.id]: true }));
                        setPendingClipUnlinks(prev => ({ ...prev, [item.id]: true }));
                        setSelectedGateway(prev => ({ ...prev, [item.id]: '' }));
                        // Update initial states to reflect the change
                        setInitialDockEditMode(prev => ({ ...prev, [item.id]: true }));
                        setInitialSelectedGateway(prev => ({ ...prev, [item.id]: '' }));
                      }}
                    >
                      {t('deactivate')}
                    </Text>
                  )}
                </Flex>
              </Box>
            </GridItem>
            {/* Clip */}
            <GridItem>
              <Box width="100%">
                <Flex direction="column" alignItems="flex-start" pl="41px">
                  <Box mb="15px">
                    <AleaClipOpenIcon size="75" />
                  </Box>
                </Flex>
                <Flex direction="column" gap={2} width="100%">
                  <Box width="100%">
                    <AleaDropdownAlpha
                      label={t('clip')}
                      value={item.serial}
                      options={[
                        { label: item.serial, value: item.serial },
                      ]}
                      onChange={() => {}}
                      placeholder={t('hardware.selectAClip')}
                      searchPlaceholder={'Name, Seriennummer, ...'}
                      isDisabled={true}
                    />
                  </Box>
                </Flex>
              </Box>
            </GridItem>
          </Grid>
        </Box>
        <Flex justifyContent="flex-end" alignItems="center" gap="16px" mt={4}>
          {/* Cancel link - only show when there are pending changes */}
          {hasPendingChanges(item.id) && !isProgressModalOpen && (
            <Text
              color="gray.500"
              fontSize="sm"
              cursor="pointer"
              textDecoration="underline"
              onClick={() => {
                setPendingRowAction({ targetRowId: item.id, action: 'close' });
                setIsUnsavedChangesModalOpen(true);
              }}
              _hover={{ color: "gray.700" }}
            >
              {t('general.cancel')}
            </Text>
          )}

          <Button
            colorScheme="alea"
            variant="solid"
            size="lg"
            onClick={() => handleSubmit(item.id)}
            w="100px"
            h="50px"
            borderRadius="20px"
            isDisabled={
              isProgressModalOpen ||
              !hasPendingChanges(item.id)
            }
            isLoading={isSubmitting}
            loadingText=""
            _disabled={{
              bg: '#E2E8F0',
              opacity: 1,
              cursor: 'not-allowed',
            }}
          >
            {!isSubmitting && (
              <Image
                src={checkIcon}
                alt="Icon"
                h="21px"
                opacity={!hasPendingChanges(item.id) ? 0.4 : 1}
              />
            )}
          </Button>
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      <AleaTable<ExtendedClip>
        columns={columns}
        data={clips as ExtendedClip[]}
        isLoading={isLoading}
        total={total}
        expandable={user?.role !== UserRole.ORGANISATION_MANAGER}
        expandedRowRender={renderExpandedRow}
        onRowClick={handleRowClick}
        noRecordsText={t('noAvailableClips')}
        usePagination={false}
        externalExpandedRowId={expandedRowId}
      />
      
      {/* Unsaved Changes Modal */}
      <AleaConfirmModal
        isOpen={isUnsavedChangesModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleSaveAndProceed}
        onCancelButtonClick={handleDiscardAndProceed}
        title={t('saveChangesPopup')}
        confirmLabel={t('general.saveChanges')}
        cancelLabel={t('general.cancel')}
      />

      {/* Progress Modal */}
      <AleaProgressModal
        isOpen={isProgressModalOpen}
        message={progressMessage}
      />
    </Box>
  );
});

AvailableClipsTable.displayName = 'AvailableClipsTable';

export default AvailableClipsTable;
